import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Grid } from "./Home";
import { COLLECTIONS, DEPARTMENTS, deptLabel, collectionLabel } from "@/data/site";
import { SlidersHorizontal, X } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";

const SORTS = [
  { key: "featured", label: "Featured" },
  { key: "newest", label: "Newest" },
  { key: "price-low", label: "Price: Low to High" },
  { key: "price-high", label: "Price: High to Low" },
];

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [meta, setMeta] = useState({ categories: [], price_min: 0, price_max: 500 });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  useSeo({
    title: "Shop All — HYEAMAN",
    description: "Browse the full HYEAMAN collection — outerwear, tailoring, hoodies, tees and accessories in luxury editorial streetwear.",
    path: "/shop",
  });

  const department = params.get("department") || "";
  const collection = params.get("collection") || "";
  const category = params.get("category") || "";
  const sort = params.get("sort") || "featured";
  const maxPrice = params.get("max_price") || "";

  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    const p = { sort };
    if (department) p.department = department;
    if (collection) p.collection = collection;
    if (category) p.category = category;
    if (maxPrice) p.max_price = maxPrice;
    const { data } = await api.get("/products", { params: p });
    setProducts(data);
    setLoading(false);
  }, [department, collection, category, sort, maxPrice]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const update = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next);
  };
  const clearAll = () => setParams(new URLSearchParams());

  const heading = collection ? collectionLabel(collection) : department ? deptLabel(department) : "All Pieces";

  const Filters = () => (
    <div className="space-y-8" data-testid="shop-filters">
      <FilterGroup title="Department" options={DEPARTMENTS.map((d) => [d.key, d.label])} active={department} onSelect={(v) => update("department", v)} testid="filter-department" />
      <FilterGroup title="Collection" options={COLLECTIONS.map((c) => [c.key, c.label])} active={collection} onSelect={(v) => update("collection", v)} testid="filter-collection" />
      <FilterGroup title="Category" options={meta.categories.map((c) => [c, c])} active={category} onSelect={(v) => update("category", v)} testid="filter-category" />
      <div>
        <p className="overline-label mb-3">Max Price</p>
        <input type="range" min={meta.price_min} max={meta.price_max} step="10"
          value={maxPrice || meta.price_max} onChange={(e) => update("max_price", e.target.value)}
          data-testid="filter-price" className="w-full accent-[#cda95c]" />
        <p className="mt-1 font-mono text-xs text-gold">C${maxPrice || meta.price_max}</p>
      </div>
      <button onClick={clearAll} data-testid="clear-filters" className="text-xs uppercase tracking-widest text-cream/50 hover:text-gold">Clear all filters</button>
    </div>
  );

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8 lg:px-12" data-testid="shop-page">
      <div className="mb-10 border-b border-white/10 pb-8">
        <p className="overline-label mb-2">The Store</p>
        <h1 className="font-black uppercase tracking-tight text-cream text-4xl sm:text-5xl">{heading}</h1>
        <p className="mt-2 text-sm text-cream/50">{products.length} pieces</p>
      </div>

      <div className="grid gap-10 lg:grid-cols-[240px_1fr]">
        <aside className="hidden lg:block"><Filters /></aside>

        <div>
          <div className="mb-6 flex items-center justify-between">
            <button onClick={() => setShowFilters(true)} data-testid="mobile-filter-toggle" className="flex items-center gap-2 border border-white/15 px-4 py-2 text-xs uppercase tracking-widest text-cream lg:hidden">
              <SlidersHorizontal size={14} /> Filters
            </button>
            <div className="ml-auto flex items-center gap-3">
              <span className="hidden text-xs uppercase tracking-widest text-cream/50 sm:inline">Sort</span>
              <select value={sort} onChange={(e) => update("sort", e.target.value)} data-testid="sort-select"
                className="border border-white/15 bg-ink px-4 py-2 text-xs uppercase tracking-widest text-cream outline-none focus:border-gold">
                {SORTS.map((s) => <option key={s.key} value={s.key} className="bg-ink">{s.label}</option>)}
              </select>
            </div>
          </div>

          {loading ? (
            <Grid>{Array.from({ length: 8 }).map((_, i) => <div key={i} className="aspect-[3/4] animate-pulse bg-ink" />)}</Grid>
          ) : products.length === 0 ? (
            <div className="flex flex-col items-center justify-center border border-white/10 py-24 text-center" data-testid="empty-results">
              <p className="font-serif text-2xl text-cream">No pieces found</p>
              <p className="mt-2 text-sm text-cream/50">Try adjusting your filters or explore the full store.</p>
              <button onClick={clearAll} className="mt-6 border border-gold px-6 py-3 text-xs uppercase tracking-widest text-gold hover:bg-gold hover:text-black">Clear filters</button>
            </div>
          ) : (
            <Grid>{products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}</Grid>
          )}
        </div>
      </div>

      {showFilters && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/80" onClick={() => setShowFilters(false)} />
          <div className="absolute right-0 top-0 h-full w-4/5 max-w-xs overflow-y-auto bg-ink p-6 reveal">
            <div className="mb-6 flex items-center justify-between">
              <h3 className="font-serif text-xl text-cream">Filters</h3>
              <button onClick={() => setShowFilters(false)} aria-label="Close"><X size={20} className="text-cream" /></button>
            </div>
            <Filters />
          </div>
        </div>
      )}
    </div>
  );
}

function FilterGroup({ title, options, active, onSelect, testid }) {
  return (
    <div data-testid={testid}>
      <p className="overline-label mb-3">{title}</p>
      <div className="flex flex-col gap-1.5">
        {options.map(([val, label]) => (
          <button key={val} onClick={() => onSelect(active === val ? "" : val)}
            className={`text-left text-sm capitalize transition ${active === val ? "text-gold" : "text-cream/60 hover:text-cream"}`}>
            {active === val ? "— " : ""}{label}
          </button>
        ))}
      </div>
    </div>
  );
}
